import { DebugOverlay } from "../app/canvas/DebugOverlay";
import { Point } from "./geom/Point";
import { Polyline } from "./geom/Polyline";

export interface Pathfinder<T> {

    search(start: T, goal: T): FoundPath<T> | null;
}

export interface PathfinderScenario<T> {

    estimate(node: T, goal: T): number;

    routesFrom(node: T): PathRoute<T>[];

    init(goal: T): void;

    cleanUp(goal: T): void;
}

export const PathRoute = <T>(dest: T, cost: number): PathRoute<T> => ({ dest, cost });
export interface PathRoute<T> {

    readonly dest: T;
    readonly cost: number;
}

export interface FoundPath<T> extends ReadonlyArray<T> {
    readonly start: T;
    readonly goal: T;
    readonly cost: number;
}

export class AStarPathfinder<T> implements Pathfinder<T> {

    public readonly scenario: PathfinderScenario<T>;

    constructor(scenario: PathfinderScenario<T>) {
        this.scenario = scenario;
    }

    public search(start: T, goal: T): FoundPath<T> | null {
        const { scenario } = this;

        const hs = new Map<T, number>();
        const gs = new Map<T, number>();
        const fs = new Map<T, number>();

        const links = new Map<T, T>();

        const opened = new Set<T>();
        const closed = new Set<T>();
        const queue = [] as T[];

        scenario.init(goal);

        // G is the distance between the current node and the start node.
        // H is the heuristic — estimated distance from the current node to the end node.
        // F is the total cost of the node.

        hs.set(start, scenario.estimate(start, goal));
        gs.set(start, 0);
        fs.set(start, gs.get(start) + hs.get(start));

        opened.add(start);
        queue.push(start);

        let cb = 0;

        while (queue.length) {

            const current = queue.pop();
            opened.delete(current);
            closed.add(current);

            // If we are at our goal, we are done
            if (current === goal) {
                // So return path working backwards
                const path = [] as T[];
                for (let n = goal; !!n; n = links.get(n)) {
                    path.push(n);
                }

                const result: any = Object.freeze(Object.assign(path.reverse(), {
                    start,
                    goal,
                    cost: gs.get(current),
                }));

                scenario.cleanUp(goal);
                return result as FoundPath<T>;
            }

            // Find all the possible routes from the current node and evaluate
            const routes = scenario.routesFrom(current);
            for (const r of routes) {
                if (closed.has(r.dest)) continue; // Ignore if we have already visited
                if (opened.has(r.dest)) {
                    // If already on open list, is the path via current cheaper?  If so change the path mapping
                    if ((gs.get(current) + r.cost) < gs.get(r.dest)) {
                        links.set(r.dest, current);
                        gs.set(r.dest, gs.get(current) + r.cost);
                        fs.set(r.dest, gs.get(r.dest) + hs.get(r.dest));
                    }
                }
                else {
                    // If we have not seen this node before, add to open list
                    opened.add(r.dest);
                    queue.push(r.dest);
                    // and link together
                    links.set(r.dest, current);
                    hs.set(r.dest, scenario.estimate(r.dest, goal));
                    gs.set(r.dest, gs.get(current) + r.cost);
                    fs.set(r.dest, gs.get(r.dest) + hs.get(r.dest));
                }
            }
            // Prioritize the queue so that the best option is at the end (so it will be next)
            queue.sort((a, b) => hs.get(b) - hs.get(a));

            if (cb++ > 10000) {
                alert('A* failed');

                // So return path working backwards
                const path = [] as Point[];
                for (let n = current; !!n; n = links.get(n)) {
                    path.push((n as any)[0]);
                }

                DebugOverlay.path('AS', new Polyline(path).toPath());

                break;
            }
        }

        // We failed.
        scenario.cleanUp(goal);
        return null;
    }
}
